/**
 * Come si legge una voce dello storico degli aggiornamenti.
 *
 * 🎯 Tenuto fuori dal componente per la solita ragione di questo progetto: il
 * frontend non ha un ambiente di prova React, quindi solo le funzioni pure si
 * provano davvero. Qui dentro sta tutto ciò che ha una regola — la traduzione
 * dei passi, il tono di una riga, cosa scrivere quando non c'è niente da
 * mostrare — e il componente si limita a disporre in tabella quello che esce.
 *
 * 🔴 **Non c'è nessun campo di testo libero da mostrare, ed è voluto.** Lo
 * storico sul server non porta i messaggi d'errore: contengono percorsi del
 * volume e comandi di riparazione, e sarebbero una mappa della macchina per
 * chiunque sia amministratore **dell'applicazione**. Il dettaglio di un guasto
 * sta nel Telegram e nel journal, cioè dove arriva solo chi ha già accesso al
 * server. Qui si dice quando, quale versione, chi ha innescato, com'è andata e
 * **a quale passo** si è fermata: è ciò che serve per decidere se chiamare
 * qualcuno, senza raccontare com'è fatta la macchina.
 */
import type { VoceStoricoAggiornamento } from '../../api/tipi-dati.ts';

/**
 * I passi dell'aggiornatore, tradotti in una frase che dica **cosa stava
 * facendo** invece del nome della variabile.
 *
 * ⚠️ Un passo sconosciuto si mostra così com'è, e non si nasconde: gli script
 * sul server si installano a mano e possono essere più nuovi
 * dell'applicazione, quindi un nome che non è in questa tabella è la
 * normalità del giorno dopo un cambio, non un difetto. Mostrarlo grezzo è
 * brutto e onesto; non mostrarlo affatto toglierebbe l'unica informazione
 * della riga.
 */
const PASSI: Record<string, string> = {
  avvio: "all'avvio, prima di cominciare",
  lettura: 'leggendo la richiesta',
  spazio: 'controllando lo spazio sul disco',
  costruzione: 'costruendo la versione nuova',
  preavviso: 'durante il preavviso ai colleghi',
  pagina: 'alzando la pagina di manutenzione',
  arresto: "fermando l'applicazione",
  backup: 'facendo il backup',
  migrazioni: 'applicando le migrazioni del database',
  avvio_nuova: 'avviando la versione nuova',
  salute: 'controllando che la versione nuova rispondesse',
  rollback: 'tornando indietro',
  pulizia: 'nella pulizia finale',
};

export function descriviPasso(passo: string | null): string | null {
  if (passo === null) return null;
  return PASSI[passo] ?? passo;
}

const ORIGINI: Record<VoceStoricoAggiornamento['origine'], string> = {
  timer: 'automatico',
  pulsante: 'chiesto da qui',
  manuale: 'a mano, sul server',
};

export function descriviOrigine(origine: VoceStoricoAggiornamento['origine']): string {
  return ORIGINI[origine];
}

export interface Esito {
  etichetta: string;
  /**
   * La classe del badge, non un tono astratto da tradurre nel componente: la
   * traduzione sarebbe logica fuori da qui, cioè nell'unico posto di questo
   * frontend che non si riesce a provare.
   *
   * ⚠️ I badge di questa applicazione **non usano mai il colore come unico
   * segnale**: qui il segnale è l'etichetta, che si legge anche in bianco e
   * nero. Il colore ripete, non informa.
   */
  badge: 'badge-completata' | 'badge-scaduta';
  /**
   * La riga sotto l'etichetta, quando c'è qualcosa da aggiungere: a quale
   * passo si è fermata, e se quella versione è stata messa da parte.
   */
  dettaglio: string | null;
}

/**
 * ⚠️ **«Bloccata» e «fallita» non sono la stessa cosa**, e distinguerle è la
 * ragione per cui questa funzione esiste invece di due ternari nel JSX.
 *
 * Un aggiornamento può fallire **senza** che la versione venga messa da parte:
 * succede quando il guasto accusa la macchina e non la versione — disco pieno,
 * backup non riuscito, pagina di manutenzione che non si alza. In quel caso
 * quella versione verrà ritentata stanotte, e scrivere «non verrà più
 * installata» sarebbe falso. Il contrario — una versione bloccata di cui non
 * si dice niente — lascerebbe un amministratore ad aspettare per sempre un
 * aggiornamento che non riparte da solo.
 */
export function descriviEsito(voce: VoceStoricoAggiornamento): Esito {
  if (voce.esito === 'riuscito') {
    return { etichetta: 'Installata', badge: 'badge-completata', dettaglio: null };
  }

  const passo = descriviPasso(voce.passo);
  const dove = passo === null ? 'Si è fermato' : `Si è fermato ${passo}`;

  return {
    etichetta: 'Non installata',
    badge: 'badge-scaduta',
    dettaglio: voce.bloccata
      ? `${dove}. Questa versione non viene più ritentata da sola: si riparte rilasciandone una nuova.`
      : `${dove}. Verrà ritentata al prossimo giro.`,
  };
}

/**
 * Che frase scrivere quando la tabella sarebbe vuota. `null` quando invece
 * qualcosa c'è, e la tabella parla da sé.
 *
 * ⚠️ **Il caso «su questa macchina non c'è nessun aggiornatore» qui non
 * esiste**, e non è una dimenticanza: da quando lo storico è la coda della
 * pagina Aggiornamenti, quel caso lo intercetta la pagina, che in cima dice
 * già «Aggiornamenti automatici non attivi» e non arriva nemmeno a disegnare
 * questa tabella. Ripeterlo qui sarebbe la stessa frase due volte nella stessa
 * schermata. Il campo `sistemaAttivo` resta nella risposta dell'API, dove
 * serve a chi la legge da fuori.
 */
export function descriviVuoto(quante: number): string | null {
  if (quante > 0) return null;

  return (
    "Il sistema di aggiornamento c'è, ma non ha ancora registrato nessun tentativo. " +
    'La prima riga comparirà dopo il primo aggiornamento.'
  );
}
